/**
 * Re-usable section blocks for product pages.
 * They keep the existing design system (dark, hairline grids, technical type).
 */
import { Check } from "lucide-react";

export function ProblemBlock({ overline = "Problem", title, text, cards }) {
  return (
    <section className="border-t border-[rgba(255,255,255,0.08)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="overline mb-4">{overline}</div>
        <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-4xl">
          {title}
        </h2>
        {text && (
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            {text}
          </p>
        )}
        {cards && cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {cards.map((c, i) => (
              <div
                key={i}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-7 hover:bg-[#0B0F10] transition-colors"
              >
                <div className="font-display text-[#18E096] text-sm mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-lg md:text-xl uppercase">{c.title}</h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function IntroBlock({ overline = "Für wen", title, text }) {
  return (
    <section className="border-t border-[rgba(255,255,255,0.08)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline mb-4">{overline}</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase leading-[1.08]">{title}</h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[#AEB7B7] text-base md:text-lg leading-relaxed whitespace-pre-line">
            {text}
          </p>
        </div>
      </div>
    </section>
  );
}

export function ExperienceBlock({ overline = "Erlebnisformate", title, text, items, highlight }) {
  return (
    <section className="border-t border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="overline mb-4">{overline}</div>
        <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-4xl">
          {title}
        </h2>
        {text && (
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            {text}
          </p>
        )}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px mt-10 border-l border-t border-[rgba(255,255,255,0.08)]">
          {items.map((it, i) => (
            <li
              key={i}
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-5 flex items-start gap-3 text-sm text-[#AEB7B7] hover:bg-[#141819] transition-colors"
            >
              <Check size={16} strokeWidth={1.5} className="text-[#18E096] mt-0.5 shrink-0" />
              <span className="text-white">{it}</span>
            </li>
          ))}
        </ul>
        {highlight && (
          <p className="mt-10 max-w-3xl text-white text-lg md:text-xl border-l-2 border-[#18E096] pl-5 leading-snug">
            {highlight}
          </p>
        )}
      </div>
    </section>
  );
}

export function AudienceBlock({ overline = "Für wen", title, cards }) {
  return (
    <section className="border-t border-[rgba(255,255,255,0.08)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="overline mb-4">{overline}</div>
        <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-4xl">
          {title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
          {cards.map((c, i) => (
            <div
              key={i}
              className="border-r border-b border-[rgba(255,255,255,0.08)] p-7 hover:bg-[#0B0F10] transition-colors"
            >
              <div className="font-display text-3xl text-[#18E096]">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display text-base md:text-lg uppercase mt-4">{c.title}</h3>
              <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BenefitsBlock({ overline = "Nutzen", title, items }) {
  return (
    <section className="border-t border-[rgba(255,255,255,0.08)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline mb-4">{overline}</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase leading-[1.08]">{title}</h2>
        </div>
        <ul className="lg:col-span-7 space-y-3">
          {items.map((x, i) => (
            <li
              key={i}
              className="flex items-start gap-4 border-b border-[rgba(255,255,255,0.08)] pb-3 text-[#AEB7B7]"
            >
              <span className="font-mono text-xs text-[#18E096] pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-white text-base md:text-lg leading-snug">{x}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
